import { c as _c } from "react/compiler-runtime";
import { Checkbox, FormInput, FormInputNumber, FormSelect, PrimaryButton, RadioGroup, RadioGroupDataInterface, SecondaryButton, Units, cents, centsToUnits, units } from '@systemeio/ui';
import { TaxBehaviorEnum, taxBehaviorLocoKeys } from 'modules/payment/tax-behavior';
import { WEIGHT_STEP } from 'modules/profile/shipping/constants/shipping-consts';
import { useEffect, useState } from 'react';
import FormTextarea from 'shared/components/form-textarea';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { useCurrencyData } from 'shared/hooks/use-currency-data';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { emptyProductErrorsState } from '../constants/product-errors';
import ProductErrorsInterface from '../types/error-response-content-interface';
import { ProductDataInterface, ProductInterface, ProductSaveInterface } from '../types/product-interface';
import { ProductVariantsErrorsInterface } from '../types/product-variant-interface';
import { filterOptions, filterVariants, validateVariants } from '../utils/product-filters';
import { getVariants } from '../utils/product-variants-utils';
import Options from './Options';
import Variants from './Variants';
import ProductBreadcrumbs from './product-breadcrumbs';
import MediaLibrary from './product-media-library';
import { useCurrency } from 'modules/payment-methods/hooks/use-currency';
const defaultNumberValue = '' as unknown as number;
interface ProductTemplateProps {
  product?: ProductInterface;
  maxProductVariants: number;
  validationErrorsNotification?: boolean;
  setValidationErrorsNotification: React.Dispatch<React.SetStateAction<boolean>>;
  onSave: (saveProductData: ProductSaveInterface) => void;
  isFetching?: boolean;
  defaultData?: Pick<ProductInterface, 'currency' | 'tax'> | undefined;
}
const ProductForm = t0 => {
  const $ = _c(285);
  const {
    product,
    maxProductVariants,
    validationErrorsNotification,
    onSave,
    isFetching,
    setValidationErrorsNotification,
    defaultData
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const [name, setName] = useState(product?.name ?? "");
  const [description, setDescription] = useState(product?.description ?? "");
  const [sku, setSku] = useState(product?.sku ?? "");
  const [tax, setTax] = useState(product?.tax ?? defaultNumberValue);
  const [weight, setWeight] = useState(product?.weight ?? defaultNumberValue);
  let t1;
  if ($[0] !== product) {
    t1 = product?.price ? centsToUnits(product.price) : units(defaultNumberValue);
    $[0] = product;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [price, setPrice] = useState(t1);
  const t2 = product?.currency ?? defaultData?.currency;
  let t3;
  if ($[2] !== t) {
    t3 = t("dashboard.products.currency.alert_message");
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      initCurrency: t2,
      currencyChangeMessage: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    currentCurrency,
    setCurrentCurrency,
    showToast
  } = useCurrency(t4);
  const [limitStock, setLimitStock] = useState(product?.limitStock ?? false);
  const [shippingFeesDisabled, setShippingFeesDisabled] = useState(product?.shippingFeesDisabled ?? false);
  const [taxBehavior, setTaxBehavior] = useState(product?.taxBehavior ?? TaxBehaviorEnum.Exclusive);
  const [stock, setStock] = useState(product?.stock ?? defaultNumberValue);
  let t5;
  if ($[7] !== product?.images) {
    t5 = product?.images ?? [];
    $[7] = product?.images;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const [images, setImages] = useState(t5);
  let t6;
  if ($[9] !== product?.options) {
    t6 = product?.options ?? [];
    $[9] = product?.options;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const [options, setOptions] = useState(t6);
  let t7;
  if ($[11] !== product?.productVariants) {
    t7 = product?.productVariants ?? [];
    $[11] = product?.productVariants;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const [variants, setVariants] = useState(t7);
  const [errors, setErrors] = useState(emptyProductErrorsState);
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {};
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const [productVariantsErrors, setProductVariantsErrors] = useState(t8);
  const currencyData = useCurrencyData();
  let t9;
  if ($[14] !== currentCurrency || $[15] !== description || $[16] !== images || $[17] !== limitStock || $[18] !== name || $[19] !== onSave || $[20] !== options || $[21] !== price || $[22] !== shippingFeesDisabled || $[23] !== sku || $[24] !== stock || $[25] !== t || $[26] !== tax || $[27] !== taxBehavior || $[28] !== variants || $[29] !== weight) {
    t9 = () => {
      const filteredOptions = filterOptions(options);
      const filteredVariants = filterVariants(variants);
      const areValidVariants = validateVariants(filteredVariants, t, setProductVariantsErrors);
      if (!areValidVariants) {
        return;
      }
      if (!currentCurrency) {
        return;
      }
      const productData = {
        name,
        description,
        tax,
        sku,
        images,
        currency: currentCurrency,
        price: price ? cents((price * 100).toFixed(0)) : cents(price),
        weight,
        options: filteredOptions,
        productVariants: filteredVariants,
        limitStock,
        shippingFeesDisabled,
        taxBehavior
      };
      if (limitStock) {
        productData.stock = stock || 0;
      }
      onSave?.({
        productData,
        setErrors,
        setVariantErrors: setProductVariantsErrors
      });
    };
    $[14] = currentCurrency;
    $[15] = description;
    $[16] = images;
    $[17] = limitStock;
    $[18] = name;
    $[19] = onSave;
    $[20] = options;
    $[21] = price;
    $[22] = shippingFeesDisabled;
    $[23] = sku;
    $[24] = stock;
    $[25] = t;
    $[26] = tax;
    $[27] = taxBehavior;
    $[28] = variants;
    $[29] = weight;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const handleSave = t9;
  let t10;
  if ($[31] !== limitStock || $[32] !== options || $[33] !== stock) {
    t10 = () => {
      setVariants(prev => {
        const newVariants = getVariants(options, prev);
        if (!limitStock && !stock) {
          return newVariants;
        }
        return newVariants.map(v => ({
          ...v,
          stock: v.stock ?? stock ?? 0
        }));
      });
    };
    $[31] = limitStock;
    $[32] = options;
    $[33] = stock;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== options) {
    t11 = [options];
    $[35] = options;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[37] !== defaultData || $[38] !== product || $[39] !== tax) {
    t12 = () => {
      if (!product && defaultData) {
        if (tax === defaultNumberValue) {
          setTax(defaultData.tax);
        }
      }
    };
    t13 = [defaultData, tax, product];
    $[37] = defaultData;
    $[38] = product;
    $[39] = tax;
    $[40] = t12;
    $[41] = t13;
  } else {
    t12 = $[40];
    t13 = $[41];
  }
  useEffect(t12, t13);
  let t14;
  if ($[42] !== errors || $[43] !== setValidationErrorsNotification) {
    t14 = () => {
      let isErrors = false;
      Object.keys(errors).forEach(el => {
        if (el === "common") {
          return;
        }
        if (el === "options") {
          if (!!Object.keys(errors[el as keyof ProductErrorsInterface]).length) {
            isErrors = true;
          }
          return;
        }
        if (!!errors[el as keyof ProductErrorsInterface]) {
          isErrors = true;
        }
      });
      setValidationErrorsNotification(isErrors);
    };
    $[42] = errors;
    $[43] = setValidationErrorsNotification;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== errors) {
    t15 = [errors];
    $[45] = errors;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  useEffect(t14, t15);
  let t16;
  if ($[47] !== t) {
    t16 = t(taxBehaviorLocoKeys[TaxBehaviorEnum.Exclusive]);
    $[47] = t;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t16) {
    t17 = {
      id: TaxBehaviorEnum.Exclusive,
      caption: t16
    };
    $[49] = t16;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== t) {
    t18 = t(taxBehaviorLocoKeys[TaxBehaviorEnum.Inclusive]);
    $[51] = t;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== t18) {
    t19 = {
      id: TaxBehaviorEnum.Inclusive,
      caption: t18
    };
    $[53] = t18;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== t17 || $[56] !== t19) {
    t20 = [t17, t19];
    $[55] = t17;
    $[56] = t19;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  const taxBehaviorOptions = t20;
  let t21;
  if ($[58] !== product) {
    t21 = product && <ProductBreadcrumbs />;
    $[58] = product;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== t) {
    t22 = t("dashboard.products.name.title");
    $[60] = t;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== t22) {
    t23 = <div className="text-text-primary mb-2 text-lg font-semibold">{t22}</div>;
    $[62] = t22;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== t) {
    t24 = t("dashboard.products.name.title");
    $[64] = t;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = e => {
      setErrors(_temp);
      setName(e.target.value);
    };
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== errors.name || $[68] !== name || $[69] !== t24) {
    t26 = <FormInput value={name} placeholder={t24} onChange={t25} maxLength={256} error={errors.name} />;
    $[67] = errors.name;
    $[68] = name;
    $[69] = t24;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== t) {
    t27 = t("dashboard.products.description.title");
    $[71] = t;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== t27) {
    t28 = <div className="text-text-primary mb-2 text-lg font-semibold">{t27}</div>;
    $[73] = t27;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== t) {
    t29 = t("dashboard.products.description.title");
    $[75] = t;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  const t30 = description ?? "";
  let t31;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = e_0 => setDescription(e_0.target.value);
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== t29 || $[79] !== t30) {
    t32 = <FormTextarea placeholder={t29} value={t30} onChange={t31} />;
    $[78] = t29;
    $[79] = t30;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== t28 || $[82] !== t32) {
    t33 = <div className="lg:my-6">{t28}{t32}</div>;
    $[81] = t28;
    $[82] = t32;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== t) {
    t34 = t("dashboard.products.sku.title");
    $[84] = t;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== t34) {
    t35 = <div className="text-text-primary my-2 text-lg font-semibold">{t34}</div>;
    $[86] = t34;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== t) {
    t36 = t("dashboard.products.sku.title");
    $[88] = t;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  let t37;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = e_1 => setSku(e_1.target.value);
    $[90] = t37;
  } else {
    t37 = $[90];
  }
  let t38;
  if ($[91] !== name || $[92] !== sku) {
    t38 = () => {
      if (!sku && name) {
        const calculatedSku = `${name.substring(0, 3).toUpperCase()}-${Math.floor(Math.random() * 1000)}`;
        setErrors(_temp2);
        setSku(calculatedSku);
      }
    };
    $[91] = name;
    $[92] = sku;
    $[93] = t38;
  } else {
    t38 = $[93];
  }
  let t39;
  if ($[94] !== errors.sku || $[95] !== sku || $[96] !== t36 || $[97] !== t38) {
    t39 = <FormInput value={sku} placeholder={t36} onChange={t37} onFocus={t38} maxLength={256} error={errors.sku} />;
    $[94] = errors.sku;
    $[95] = sku;
    $[96] = t36;
    $[97] = t38;
    $[98] = t39;
  } else {
    t39 = $[98];
  }
  let t40;
  if ($[99] !== t35 || $[100] !== t39) {
    t40 = <div className="flex w-full flex-col">{t35}{t39}</div>;
    $[99] = t35;
    $[100] = t39;
    $[101] = t40;
  } else {
    t40 = $[101];
  }
  let t41;
  if ($[102] !== t) {
    t41 = t("dashboard.products.list.tax_header");
    $[102] = t;
    $[103] = t41;
  } else {
    t41 = $[103];
  }
  let t42;
  if ($[104] !== t41) {
    t42 = <div className="text-text-primary my-2 text-lg font-semibold">{t41}</div>;
    $[104] = t41;
    $[105] = t42;
  } else {
    t42 = $[105];
  }
  let t43;
  if ($[106] !== t) {
    t43 = t("dashboard.products.list.tax_header");
    $[106] = t;
    $[107] = t43;
  } else {
    t43 = $[107];
  }
  let t44;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = value => {
      setErrors(_temp3);
      setTax(value);
    };
    $[108] = t44;
  } else {
    t44 = $[108];
  }
  let t45;
  if ($[109] !== errors.tax || $[110] !== t43 || $[111] !== tax) {
    t45 = <FormInputNumber value={tax} placeholder={t43} onChange={t44} toFixed={1} min={0} max={99} error={errors.tax} />;
    $[109] = errors.tax;
    $[110] = t43;
    $[111] = tax;
    $[112] = t45;
  } else {
    t45 = $[112];
  }
  let t46;
  if ($[113] !== t42 || $[114] !== t45) {
    t46 = <div className="flex w-full flex-col">{t42}{t45}</div>;
    $[113] = t42;
    $[114] = t45;
    $[115] = t46;
  } else {
    t46 = $[115];
  }
  let t47;
  if ($[116] !== t40 || $[117] !== t46) {
    t47 = <div className="lg:my-6"><div className="flex flex-col justify-center lg:flex-row lg:gap-8">{t40}{t46}</div></div>;
    $[116] = t40;
    $[117] = t46;
    $[118] = t47;
  } else {
    t47 = $[118];
  }
  let t48;
  if ($[119] !== t) {
    t48 = t("dashboard.payment.tax_behavior.title");
    $[119] = t;
    $[120] = t48;
  } else {
    t48 = $[120];
  }
  let t49;
  if ($[121] !== t48) {
    t49 = <div className="text-text-primary my-2 text-lg font-semibold">{t48}</div>;
    $[121] = t48;
    $[122] = t49;
  } else {
    t49 = $[122];
  }
  let t50;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = value_0 => setTaxBehavior(value_0);
    $[123] = t50;
  } else {
    t50 = $[123];
  }
  let t51;
  if ($[124] !== errors.taxBehavior || $[125] !== taxBehavior || $[126] !== taxBehaviorOptions) {
    t51 = <RadioGroup options={taxBehaviorOptions} selected={taxBehavior} onChange={t50} error={errors.taxBehavior} />;
    $[124] = errors.taxBehavior;
    $[125] = taxBehavior;
    $[126] = taxBehaviorOptions;
    $[127] = t51;
  } else {
    t51 = $[127];
  }
  let t52;
  if ($[128] !== t49 || $[129] !== t51) {
    t52 = <div className="lg:my-6"><div className="flex flex-col justify-center lg:flex-row lg:gap-8"><div className="flex w-full flex-col">{t49}{t51}</div></div></div>;
    $[128] = t49;
    $[129] = t51;
    $[130] = t52;
  } else {
    t52 = $[130];
  }
  let t53;
  if ($[131] !== t) {
    t53 = t("global.currency");
    $[131] = t;
    $[132] = t53;
  } else {
    t53 = $[132];
  }
  let t54;
  if ($[133] !== t53) {
    t54 = <div className="text-text-primary my-2 text-lg font-semibold">{t53}</div>;
    $[133] = t53;
    $[134] = t54;
  } else {
    t54 = $[134];
  }
  let t55;
  if ($[135] !== product || $[136] !== setCurrentCurrency || $[137] !== showToast) {
    t55 = value_1 => {
      setErrors(_temp4);
      setCurrentCurrency(value_1);
      if (!!product) {
        showToast();
      }
    };
    $[135] = product;
    $[136] = setCurrentCurrency;
    $[137] = showToast;
    $[138] = t55;
  } else {
    t55 = $[138];
  }
  let t56;
  if ($[139] !== t) {
    t56 = t("dashboard.product.currency_select");
    $[139] = t;
    $[140] = t56;
  } else {
    t56 = $[140];
  }
  let t57;
  if ($[141] !== currencyData || $[142] !== currentCurrency || $[143] !== errors.currency || $[144] !== t55 || $[145] !== t56) {
    t57 = <FormSelect onChange={t55} value={currentCurrency} data={currencyData} placeholder={t56} error={errors.currency} withoutCloseIcon={true} />;
    $[141] = currencyData;
    $[142] = currentCurrency;
    $[143] = errors.currency;
    $[144] = t55;
    $[145] = t56;
    $[146] = t57;
  } else {
    t57 = $[146];
  }
  let t58;
  if ($[147] !== t54 || $[148] !== t57) {
    t58 = <div className="flex w-full flex-col">{t54}{t57}</div>;
    $[147] = t54;
    $[148] = t57;
    $[149] = t58;
  } else {
    t58 = $[149];
  }
  let t59;
  if ($[150] !== t) {
    t59 = t("global.price");
    $[150] = t;
    $[151] = t59;
  } else {
    t59 = $[151];
  }
  let t60;
  if ($[152] !== t59) {
    t60 = <div className="text-text-primary my-2 text-lg font-semibold">{t59}</div>;
    $[152] = t59;
    $[153] = t60;
  } else {
    t60 = $[153];
  }
  let t61;
  if ($[154] !== t) {
    t61 = t("global.price");
    $[154] = t;
    $[155] = t61;
  } else {
    t61 = $[155];
  }
  let t62;
  if ($[156] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = value_2 => {
      setErrors(_temp5);
      setPrice(value_2 as Units);
    };
    $[156] = t62;
  } else {
    t62 = $[156];
  }
  let t63;
  if ($[157] !== errors.price || $[158] !== price || $[159] !== t61) {
    t63 = <FormInputNumber value={price} placeholder={t61} onChange={t62} min={0} error={errors.price} />;
    $[157] = errors.price;
    $[158] = price;
    $[159] = t61;
    $[160] = t63;
  } else {
    t63 = $[160];
  }
  let t64;
  if ($[161] !== t60 || $[162] !== t63) {
    t64 = <div className="flex w-full flex-col">{t60}{t63}</div>;
    $[161] = t60;
    $[162] = t63;
    $[163] = t64;
  } else {
    t64 = $[163];
  }
  let t65;
  if ($[164] !== t58 || $[165] !== t64) {
    t65 = <div className="lg:my-6"><div className="flex flex-col justify-center lg:flex-row lg:gap-8">{t58}{t64}</div></div>;
    $[164] = t58;
    $[165] = t64;
    $[166] = t65;
  } else {
    t65 = $[166];
  }
  let t66;
  if ($[167] !== t) {
    t66 = t("dashboard.products.weight.title");
    $[167] = t;
    $[168] = t66;
  } else {
    t66 = $[168];
  }
  let t67;
  if ($[169] !== t66) {
    t67 = <div className="text-text-primary my-2 text-lg font-semibold">{t66}</div>;
    $[169] = t66;
    $[170] = t67;
  } else {
    t67 = $[170];
  }
  let t68;
  if ($[171] !== t) {
    t68 = t("dashboard.products.weight.title");
    $[171] = t;
    $[172] = t68;
  } else {
    t68 = $[172];
  }
  let t69;
  if ($[173] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = value_3 => {
      setErrors(_temp6);
      setWeight(value_3);
    };
    $[173] = t69;
  } else {
    t69 = $[173];
  }
  let t70;
  if ($[174] !== t) {
    t70 = t("dashboard.products.weight.gram_short");
    $[174] = t;
    $[175] = t70;
  } else {
    t70 = $[175];
  }
  let t71;
  if ($[176] !== errors.weight || $[177] !== t68 || $[178] !== t70 || $[179] !== weight) {
    t71 = <FormInputNumber value={weight} placeholder={t68} onChange={t69} min={0} addonAfter={t70} step={WEIGHT_STEP} error={errors.weight} />;
    $[176] = errors.weight;
    $[177] = t68;
    $[178] = t70;
    $[179] = weight;
    $[180] = t71;
  } else {
    t71 = $[180];
  }
  let t72;
  if ($[181] !== t67 || $[182] !== t71) {
    t72 = <div className="lg:my-6"><div className="flex flex-col justify-center lg:flex-row lg:gap-8"><div className="flex w-full flex-col">{t67}{t71}</div></div></div>;
    $[181] = t67;
    $[182] = t71;
    $[183] = t72;
  } else {
    t72 = $[183];
  }
  let t73;
  if ($[184] !== t) {
    t73 = t("dashboard.products.stock.title");
    $[184] = t;
    $[185] = t73;
  } else {
    t73 = $[185];
  }
  let t74;
  if ($[186] !== t73) {
    t74 = <div className="text-text-primary mb-2 text-lg font-semibold">{t73}</div>;
    $[186] = t73;
    $[187] = t74;
  } else {
    t74 = $[187];
  }
  let t75;
  if ($[188] !== t) {
    t75 = t("dashboard.products.allow_stock_description");
    $[188] = t;
    $[189] = t75;
  } else {
    t75 = $[189];
  }
  let t76;
  if ($[190] !== limitStock || $[191] !== t75) {
    t76 = <div className="mt-4"><Checkbox value={limitStock} onChange={setLimitStock} label={t75} /></div>;
    $[190] = limitStock;
    $[191] = t75;
    $[192] = t76;
  } else {
    t76 = $[192];
  }
  let t77;
  if ($[193] !== t74 || $[194] !== t76) {
    t77 = <div className="my-2 flex w-full flex-col">{t74}{t76}</div>;
    $[193] = t74;
    $[194] = t76;
    $[195] = t77;
  } else {
    t77 = $[195];
  }
  const t78 = stock ?? 0;
  let t79;
  if ($[196] !== t) {
    t79 = t("dashboard.products.stock.placeholder");
    $[196] = t;
    $[197] = t79;
  } else {
    t79 = $[197];
  }
  let t80;
  if ($[198] === Symbol.for("react.memo_cache_sentinel")) {
    t80 = value_4 => {
      setErrors(_temp7);
      setStock(value_4);
    };
    $[198] = t80;
  } else {
    t80 = $[198];
  }
  const t81 = !limitStock || variants.length > 0;
  let t82;
  if ($[199] !== errors.stock || $[200] !== t78 || $[201] !== t79 || $[202] !== t81) {
    t82 = <div className="mb-2 mt-11 flex w-full flex-col"><FormInputNumber value={t78} placeholder={t79} onChange={t80} error={errors.stock} disabled={t81} type="integer" /></div>;
    $[199] = errors.stock;
    $[200] = t78;
    $[201] = t79;
    $[202] = t81;
    $[203] = t82;
  } else {
    t82 = $[203];
  }
  let t83;
  if ($[204] !== t77 || $[205] !== t82) {
    t83 = <div className="flex flex-col justify-center lg:flex-row lg:gap-8">{t77}{t82}</div>;
    $[204] = t77;
    $[205] = t82;
    $[206] = t83;
  } else {
    t83 = $[206];
  }
  let t84;
  if ($[207] !== t) {
    t84 = t("dashboard.products.shipping.title");
    $[207] = t;
    $[208] = t84;
  } else {
    t84 = $[208];
  }
  let t85;
  if ($[209] !== t84) {
    t85 = <div className="text-text-primary mb-2 text-lg font-semibold">{t84}</div>;
    $[209] = t84;
    $[210] = t85;
  } else {
    t85 = $[210];
  }
  const t86 = errors.shippingFeesDisabled;
  let t87;
  if ($[211] !== t) {
    t87 = t("dashboard.products.shipping.disable_fees");
    $[211] = t;
    $[212] = t87;
  } else {
    t87 = $[212];
  }
  let t88;
  if ($[213] !== errors.shippingFeesDisabled || $[214] !== shippingFeesDisabled || $[215] !== t87) {
    t88 = <div className="mt-4"><Checkbox error={t86} value={shippingFeesDisabled} onChange={setShippingFeesDisabled} label={t87} /></div>;
    $[213] = errors.shippingFeesDisabled;
    $[214] = shippingFeesDisabled;
    $[215] = t87;
    $[216] = t88;
  } else {
    t88 = $[216];
  }
  let t89;
  if ($[217] !== t85 || $[218] !== t88) {
    t89 = <div className="my-2 flex w-full flex-col">{t85}{t88}</div>;
    $[217] = t85;
    $[218] = t88;
    $[219] = t89;
  } else {
    t89 = $[219];
  }
  let t90;
  if ($[220] !== t83 || $[221] !== t89) {
    t90 = <div className="lg:my-6">{t83}{t89}</div>;
    $[220] = t83;
    $[221] = t89;
    $[222] = t90;
  } else {
    t90 = $[222];
  }
  let t91;
  if ($[223] !== t23 || $[224] !== t26 || $[225] !== t33 || $[226] !== t47 || $[227] !== t52 || $[228] !== t65 || $[229] !== t72 || $[230] !== t90) {
    t91 = <div className="bg-brand-primary-subtle m-6 rounded-lg p-6">{t23}{t26}{t33}{t47}{t52}{t65}{t72}{t90}</div>;
    $[223] = t23;
    $[224] = t26;
    $[225] = t33;
    $[226] = t47;
    $[227] = t52;
    $[228] = t65;
    $[229] = t72;
    $[230] = t90;
    $[231] = t91;
  } else {
    t91 = $[231];
  }
  let t92;
  if ($[232] !== t) {
    t92 = t("dashboard.products.media.title");
    $[232] = t;
    $[233] = t92;
  } else {
    t92 = $[233];
  }
  let t93;
  if ($[234] !== t92) {
    t93 = <div className="text-text-primary mb-2 text-lg font-semibold">{t92}</div>;
    $[234] = t92;
    $[235] = t93;
  } else {
    t93 = $[235];
  }
  let t94;
  if ($[236] !== images) {
    t94 = <MediaLibrary images={images} setImages={setImages} />;
    $[236] = images;
    $[237] = t94;
  } else {
    t94 = $[237];
  }
  let t95;
  if ($[238] !== t93 || $[239] !== t94) {
    t95 = <div className="bg-brand-primary-subtle m-6 rounded-lg p-6">{t93}{t94}</div>;
    $[238] = t93;
    $[239] = t94;
    $[240] = t95;
  } else {
    t95 = $[240];
  }
  let t96;
  if ($[241] !== t) {
    t96 = t("dashboard.products.options.title");
    $[241] = t;
    $[242] = t96;
  } else {
    t96 = $[242];
  }
  let t97;
  if ($[243] !== t96) {
    t97 = <div className="text-text-primary mb-2 text-lg font-semibold">{t96}</div>;
    $[243] = t96;
    $[244] = t97;
  } else {
    t97 = $[244];
  }
  let t98;
  if ($[245] !== errors.options || $[246] !== maxProductVariants || $[247] !== options) {
    t98 = <Options options={options} setOptions={setOptions} errors={errors.options} maxProductVariants={maxProductVariants} setErrors={setErrors} />;
    $[245] = errors.options;
    $[246] = maxProductVariants;
    $[247] = options;
    $[248] = t98;
  } else {
    t98 = $[248];
  }
  let t99;
  if ($[249] !== t97 || $[250] !== t98) {
    t99 = <div className="bg-brand-primary-subtle m-6 rounded-lg p-6">{t97}{t98}</div>;
    $[249] = t97;
    $[250] = t98;
    $[251] = t99;
  } else {
    t99 = $[251];
  }
  let t100;
  if ($[252] !== limitStock || $[253] !== productVariantsErrors || $[254] !== stock || $[255] !== t || $[256] !== variants) {
    t100 = !!variants.length && <div className="bg-brand-primary-subtle m-6 rounded-lg p-6"><div className="text-text-primary mb-2 text-lg font-semibold">{t("dashboard.products.variants.title")}</div><Variants allowStock={limitStock} stock={stock ?? 0} variants={variants} setVariants={setVariants} errors={productVariantsErrors} setErrors={setProductVariantsErrors} /></div>;
    $[252] = limitStock;
    $[253] = productVariantsErrors;
    $[254] = stock;
    $[255] = t;
    $[256] = variants;
    $[257] = t100;
  } else {
    t100 = $[257];
  }
  let t101;
  if ($[258] !== errors.common) {
    t101 = errors.common?.map(_temp8);
    $[258] = errors.common;
    $[259] = t101;
  } else {
    t101 = $[259];
  }
  let t102;
  if ($[260] !== t || $[261] !== validationErrorsNotification) {
    t102 = validationErrorsNotification && <p className="text-danger ml-auto px-6 text-sm">{t("product.validation.notification.title")}</p>;
    $[260] = t;
    $[261] = validationErrorsNotification;
    $[262] = t102;
  } else {
    t102 = $[262];
  }
  let t103;
  if ($[263] !== t) {
    t103 = t("confirmation_modal.cancel");
    $[263] = t;
    $[264] = t103;
  } else {
    t103 = $[264];
  }
  let t104;
  if ($[265] !== t103) {
    t104 = <SecondaryButton as={LinkWithoutPrefetch} href="/products" width="large">{t103}</SecondaryButton>;
    $[265] = t103;
    $[266] = t104;
  } else {
    t104 = $[266];
  }
  let t105;
  if ($[267] !== t) {
    t105 = t("global.save");
    $[267] = t;
    $[268] = t105;
  } else {
    t105 = $[268];
  }
  let t106;
  if ($[269] !== handleSave || $[270] !== isFetching || $[271] !== t105) {
    t106 = <PrimaryButton width="large" onClick={handleSave} isFetching={isFetching}>{t105}</PrimaryButton>;
    $[269] = handleSave;
    $[270] = isFetching;
    $[271] = t105;
    $[272] = t106;
  } else {
    t106 = $[272];
  }
  let t107;
  if ($[273] !== t104 || $[274] !== t106) {
    t107 = <div className="flex justify-end gap-4 p-6">{t104}{t106}</div>;
    $[273] = t104;
    $[274] = t106;
    $[275] = t107;
  } else {
    t107 = $[275];
  }
  let t108;
  if ($[276] !== t100 || $[277] !== t101 || $[278] !== t102 || $[279] !== t107 || $[280] !== t21 || $[281] !== t91 || $[282] !== t95 || $[283] !== t99) {
    t108 = <div className="2xl:1/2 m-auto max-w-[900px] xl:w-5/6">{t21}{t91}{t95}{t99}{t100}{t101}{t102}{t107}</div>;
    $[276] = t100;
    $[277] = t101;
    $[278] = t102;
    $[279] = t107;
    $[280] = t21;
    $[281] = t91;
    $[282] = t95;
    $[283] = t99;
    $[284] = t108;
  } else {
    t108 = $[284];
  }
  return t108;
};
export default ProductForm;
function _temp(prev_0) {
  return {
    ...prev_0,
    name: ""
  };
}
function _temp2(prev_1) {
  return {
    ...prev_1,
    sku: ""
  };
}
function _temp3(prev_2) {
  return {
    ...prev_2,
    tax: ""
  };
}
function _temp4(prev_3) {
  return {
    ...prev_3,
    currency: ""
  };
}
function _temp5(prev_4) {
  return {
    ...prev_4,
    price: ""
  };
}
function _temp6(prev_5) {
  return {
    ...prev_5,
    weight: ""
  };
}
function _temp7(prev_6) {
  return {
    ...prev_6,
    stock: ""
  };
}
function _temp8(error, index) {
  return <p className="text-danger px-6 text-sm" key={index}>{error}</p>;
}